import { DOCUMENT } from '@angular/common';
import { DestroyRef, ErrorHandler, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { ThemeService } from '@core/services/theme.service';
import {
  EMPTY,
  Observable,
  animationFrameScheduler,
  catchError,
  distinctUntilChanged,
  filter,
  from,
  map,
  merge,
  observeOn,
  startWith,
  switchMap,
} from 'rxjs';
import { NativeShellService } from './native-shell.service';

interface ShellPalette {
  background: string;
  text: string;
  secondary: string;
  primary: string;
  onPrimary: string;
}

/** Projects the PWA's selected theme and actual CSS palette to its native host. */
@Injectable({ providedIn: 'root' })
export class NativePresentationService {
  private readonly shell = inject(NativeShellService);
  private readonly theme = inject(ThemeService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly destroy = inject(DestroyRef);
  private readonly errors = inject(ErrorHandler);
  private started = false;

  public start(): void {
    if (this.started || !this.shell.present) return;
    this.started = true;
    const classChanges = new Observable<void>((subscriber) => {
      const observer = new MutationObserver(() => subscriber.next());
      observer.observe(this.document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
      return () => observer.disconnect();
    });
    from(this.shell.initialize())
      .pipe(
        switchMap((capabilities) =>
          capabilities?.presentation
            ? merge(
                classChanges,
                this.theme.theme$,
                this.router.events.pipe(
                  filter((event) => event instanceof NavigationEnd),
                ),
              ).pipe(
                startWith(null),
                // Measure after route/theme rendering; a delay is never used as readiness.
                observeOn(animationFrameScheduler),
                filter(() =>
                  ['light', 'dark'].some((theme) =>
                    this.document.documentElement.classList.contains(theme),
                  ),
                ),
                map(() => this.snapshot()),
                distinctUntilChanged(
                  (a, b) => JSON.stringify(a) === JSON.stringify(b),
                ),
                switchMap((value) =>
                  new Observable<void>((subscriber) => {
                    const controller = new AbortController();
                    void this.shell
                      .call(
                        'shell.present',
                        value,
                        undefined,
                        controller.signal,
                      )
                      .then(
                        () => {
                          subscriber.next();
                          subscriber.complete();
                        },
                        (error) => subscriber.error(error),
                      );
                    return () => controller.abort();
                  }).pipe(
                    catchError((error) => {
                      this.errors.handleError(error);
                      return EMPTY;
                    }),
                  ),
                ),
              )
            : EMPTY,
        ),
        catchError((error) => {
          this.errors.handleError(error);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroy),
      )
      .subscribe();
  }

  private snapshot(): Record<string, unknown> {
    return {
      theme: this.theme.selectedTheme,
      dark: this.document.documentElement.classList.contains('dark'),
      light: this.palette('light'),
      darkPalette: this.palette('dark'),
      ready: this.router.lastSuccessfulNavigation !== null,
    };
  }

  private palette(theme: 'light' | 'dark'): ShellPalette {
    const element = this.document.createElement('div');
    element.className = theme;
    element.style.display = 'none';
    this.document.body.appendChild(element);
    try {
      const style = getComputedStyle(element);
      const color = (token: string): string => {
        const value = style.getPropertyValue(`--md-sys-color-${token}`).trim();
        const match = /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/.exec(
          value,
        );
        if (!match || match.slice(1).some((part) => Number(part) > 255))
          throw new Error(`Native appearance palette unavailable: ${token}`);
        return (
          '#' +
          match
            .slice(1)
            .map((part) => Number(part).toString(16).padStart(2, '0'))
            .join('')
        );
      };
      return {
        background: color('background'),
        text: color('on-surface'),
        secondary: color('on-surface-variant'),
        primary: color('primary'),
        onPrimary: color('on-primary'),
      };
    } finally {
      element.remove();
    }
  }
}
